import {
  assertEvent,
  assign,
  fromPromise,
  sendTo,
  setup,
  type SnapshotFrom,
} from 'xstate';
import type {IEvent} from '../../types/IEvent';
import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getReasons,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';


export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getReasons: fromPromise(async () => getReasons()),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        const interviewerHash = await getShortSha256Hash(
          `interviewer_${input.eventId}`,
        );
        if (interviewerHash === input.attendee) {
          return 'INTERVIEWER';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
          aid: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; reason: string; aid: string};
      }) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return (context.eventDetails as IEvent)?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellationAndNotSequential: ({context}) => {
      const sequentialEvent =
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id);
      return context.currentRoute === 'cancel' && !sequentialEvent;
    },
    isRescheduleAndNotSequential: ({context}) => {
      const sequentialEvent =
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id);
      return context.currentRoute === 'reschedule' && !sequentialEvent;
    },
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!(context.eventDetails as IEvent)?.did_cancellation_notice_pass,
    isAttendeeAvailable: ({context}) => !!context.attendee,
    isHostOrInterviewer: ({context}) =>
      context.owner === 'HOST' || context.owner === 'INTERVIEWER',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
    isSequentialEvent: ({context}) => {
      // Check if eventDetails is an array  for booking sequence OR event has group_id for any edit booking
      return (
        Array.isArray(context.eventDetails as IEvent[]) ||
        Boolean((context.eventDetails as IEvent)?.group_id)
      );
    },
    isRequestMoreAvailabilitySuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'RequestMoreAvailabilitySuccess';
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAHADYAzOSYBOAExMALBLEB2KWMkKANCACeiALSbNE+duVKlszQFYmEgL6vDaTOVgY8ybGY2JBBOHj4BIVEEUzEZdXIZBRlHCxkpKQks1UMTGMcZOXUlVQySnSltd090DB8-AKwGMWCOLl5+QRDo0wlHJXJHZyYZCUVpKQUJXLMCoolyqXKxSqlqkC86339ApVbQ9oiu0GiV8lUlZQ0rqbixGfylKwvNC4VNJk-Stw8N2vI+ABbMAALwEYAAMqg8BBIE1WEIwh1It0zNpnk5rjIVBIFP0HqYUkx5GIFKoZDYxKpkqp1ptyMgwHhYAJYFCYdwiFgIODyJyAG6oADWYAZ-0ZzNZ7IgnKgCAFqAAxnROkEgojDp0orMznFVOS7N91DljGZClJyE5RhTJGICkxNHTxUyWQQ2dCZVyeQRRQqRWLvBLXe6OUR5QRBcqjmqWhrwlrUfldfEDeUJMaCaNVJaChIbRp7Y7fvSg1KPbLuby-aKSy6y6G5Qqo6rWAw9nHkccRDqLXrU0b0wSDTnrZpbYWnYG627pRWwMg0MhyFR8BgAGaoZCAgN1Usz8thpsqgRqhEhJFHbVJ3sp7FpjOmmLqYlWvNjgsyB2T3fT2AAUQXTcsEZDBkCMdVz01FETlmCxyD6J5P2tKRPikAknisV98ztT8ixqbwgVBcEAAVuEVEVkCwQiwR9HwwAwCC2njaDuxiClHHIKRSgKFCmCUeI7DQx9TFUD5Bk0KY7CyO882-cgwH5MACAwWcvSrCNhRrf4FKUlSD0bDTmxPVszyYzsr1MXNzi41QCnsWQ7E0Al+kw3NsInYttMU5TVKgLB50XZdVw3Lcd3k7y9IbcNI2PAhT32C8Exg-JRms-U7IyO8nOE1ILSw98cK-TzvFgOAeAEXzK1o6swtK2ByoIXzoqVWL4o7S9E2cMRyC0EZFHGJg9EcB4KSsOw1DEB1VEUJ45LqhrKoCzcgroELt3pebOiao9oxMhKoK7U4+IURJCnsxwFDEJQnAeFQOOxJJUk0S4qTxOSdOUgAVMBARXOhIX0qrfQ0-16Q+jBvt+1cAainaWxYRiDmYw7xEmbMeMmvjVCpJxsryPMBiYC6KUmZ7cscd6Ishv76EWwClxptawvB6noe2wzWr29qktYpQ7QtGQqSURx9RSaaFCEvIpBF8gFGUK7HLJSQKeKupYXoLdOTAAB5AB3H1KO9YHBVB-51fnQEtb1g3mqMuKucg5GLP6TR4NEwbLAuj5BYJJZs3y8dcMp3TfIAxdgPosDEcSlieie+CXOUCxBqGaZhOJex0i4wp0me15Bbmsqto9OFo4Oq91CsSpjvsbG+Ikh5KkwyRpau7RRZkQv6uLmFS9jR3zMTKl4kSQbhekMYCjxxBMjkT4aR0PFRK0LuGrDoCQKj0ykcH5K8w45O8X6EZJj5h4hZ64WibJS6FlJOSACN0CFWUADEcFQXWgb5EHRSf4U34f11mXJ2Q8uJWDlp+ak6QlZknPldbq7tbi4gyBIV4j9n6AM-t-Gq-8X5EHfp-Zo+1QHJRUBScg40bSLz4ukeBKhziOC0NibQKRM4YIAQQoBODf7kDwVg4B7YB4dTIY4JYPUyaTWUIoJY9DiS2WYddWQ-RZAcPwVAQhX98BEFIBQagtB6AgN3rzXqnFpDxAKBLfeMgHjpgGKSWyg1yTTSWHhP43gWY-RpmAdelFN7gW3jHFGCByFz2GCLZIygDQPBFt1HE5oNAn36HJT+BtpSl0CeXRMvQdDwRKJ8JgqwdALAeO8Dik17JZDUNaTuqtyCpPnOkiATR+5mREaxeIxI5ZyySZka4Jo8hTHkRJPoeIVgEyUCk-WjSS7NLbCQ4x0QFAOk4isXiOh1By3uI+IZ5wJKaHiPnZ6986kNOQE0poMgFntNOBSOQ2J1AtySFaSWiAyk9T4iLaagtrqXDUbKAAysQRUioyrwmuTzU4Y4TpaCeOSV4csRZpzyL1dGsguJ50uCLWp+E6j8KIECkFYLiHc1jogO6J0ybt2uNIAZ4gxwDFsqwz8DpFAXTkjAA2-0ACCGB6AEFhGAHhJstLeE5fOHlfKlKCttpzBGmTSGsS0BkRIHxnCVGumOWRj5SRMPErYB0qQVC0jqcqAgoKcCrk6Jon+vBwWkuCbEakB9rpMPTKMXEtlz6fm6oUtQCx+IS2UXJM1FqrUCBtZyO1JLhGQrMAg7M8RKivG+A6O03rBryBQsUQNmUVa4vIKGsAlrYqRoINGoRbS40xDGAMIkCxPnPXGBm312aA1JDzSGwgYbS1AIZNOV+m5txrQjpKAgg7QqwGIA-S2DEFWLPjZIV2-RrrvAsI4DQXqdUbqsC4UaSRBYfAWF281xbw3jr7XuCd24p0zt4HwNS1VeH0iLSWo4Nqr1Dp8NO2dD6DIxV2vKiFZKYi6DGLLFQ7wRj6i4moc+eIBYfEUAh14mQ3Evu7We3tn9+1juvd+u9fK5z0xWuuL9GHT1vutZegdX7b2-tlLKwDRibloglpfRQ2MxYrEGufRe8gsVxHHlinF7i6ivvPR+2joUlp+Okzen9vAWPVtMMkIoGEMLqDEakc+QxuqWNSGpy6yQ1h1MZLARUAALSAxAcBgBtTgbgs6ABymAABKTIrMZOA8EuwB8XHSMesLUS58LgDD83ZCecQshyXM15iAtn7N9scy59znnrNzNaTvVjCAhh5TEdjDKcs7DerlpQzZGRs4OXQ86CzGXEsOac7wVzGAPN4Hi00St2Xq1jG6nLLIlwuKTRWEoULdhKHC0i5kaLPwC1xfq3ZqTeGv0js-ZOxTc6fPO0pfEFYYGUKpHJMNHVtbTpxAsBJFIcRTNzbgPFhrNHlvrcI3+4VmkwrzZs4tx7rp8P0fvYxuGxkgMOqvCLexmRlnpDmAcvj0hL7Jz5hYdQBczN3YW0lnDa2FMveI4FRm5Hav3e+1j+TBGGOHg5sx+dOXwc9Uh0kwosOdWZF7JqviWhXh5jELF9HX3Me61w79r9snR3C+e7O5TIGuPyGxqSS6G7xraHPgc7MbcPhI656j27dX+c2vxRo7hRsf4ir4ZgrhRCafVvOydJhJRnWFLKdslFTDuoUm+VEmRqRee64SyTwXBvNFvf9IHoBMaq0gdJILUelIpIlGkLpkossLjkg9sZvmPvicC7N5ww32DtEkDIKKfR-0pfBMmgkIYLDcROEyC4XTl1rKTfXRsq6meMdlujVbkDlkD6KBWFsieZNz6XDkPxe3hTppZG92j33D2cNRsCFloJV47A7ar6nQrkhRs6suCdZIqQjWfMQv8ogABhTDOBr1YANxfyjf2Ntl6vEkYkVI7TvAltdQWBhHxI6sLoGkOWSwA5GrbwW-S-IFXHR9Y2d7ekcAyjSAinf9FqanLbbJXEYkdIWyQWZ1HOaeEJFQAYC6I1DQchKoOpeAi1RAgHLkWTUjJmOA83KAO-KgjbP9JjeGJ-dAk6fUImfZdMZweIGxX-ZDTiFwbICkJwS6U-Zgy-XxG-Jglg4tB-QjLg5KESPVPMIbLIWwXQHfPIY1OQdMJYTIUoPg0YOSOgflQVAAcSUglXoGaU+z91FCgFQDUKVWfEtG-31EQjt1eQQEkBSFlmWQ13eCWGkLqSsOlTADADsK5UcKwAk1inIDcI8NOFEldkGkFj6DVQ0EHF-xUF9TxBTS-ypDEXcF+AIFQFhHgBCE2FB2yQH0wIbUFh3XHmOzyHMCukoVzHf052XjmgaAwEaPUPfwtAlj4iJjtFoRCxyk-E4mRzsCukPUGjkmIDdGoE4ACEgAACE0BdZSpkBRjWJYghCJE+gmFtAPgph8CRJM1LgiYLpFBc4xgTUC1qJwQmkTiegNBuoK8Dk0E+gJo7j9Q+tTDliJhb5ecx0QxPQoAfizBrg9kT53gBw6UYhhZ-jqQHQPhShPxpYbsxMhdWRfFESnx+JToUIbA91ShSh0IsgRw3xA4ioPinMiIfRSJyJ5xySejClINBp0xLp69hJ9RXYUJsQyhKgkhLp3jiTwZfJyTegk8wSU5tAHJrpdMjCjV9QLhBYtAhhV4e54TyTHkJFygnjqQnhlcTsig9tdA5hA05SwYqYvE2Z9JeTJhfVsRaTpjRlG5xF0x+pcpPgroiT6RzZNYfRrYeTY0e9kSdBa4ngvYRhndEBniPlOM+hYUUxg4fJ9IyS4zHUjtzgxgDk9Nll3g0yYgM50Vs5zQ85SgMgjSKpZlTSyseMDl7SLAAirjLQ91A1rRJpQCtgi4BBCyI9y8RgBhMgXoxFUN48xtL5nALpqRt9JBnT-hQ9P5PTnoeomEyRcSHR7B9C3lqREgVFxwFh0o8yIY3T-oJzusQMkc55BpWd4hLoMgYlShLR+I0gN1h45Ypk0k2yiyLJLBMJbIJ50hc4ChbEZZSQ0EsVcobJC4ABHYgXSbgPAHAP8CKP8GUDAFrblS1T+SAckrIPrT2QabIz4HTHVTjeCNlbGZIf1bEFsggP8YQKgbgRkCAU0tBShSoKYbFA5RyToxAT2M7e3GHD-GQwlUFeqXkg5OQfoN4PEPdLGPjTNT4MmcWB0aWBQDlew5ASVaw2I8klQSoHqII4eKfJ4eDdIc4JsgAsWZsupUqDCrCnC3YpghSsqZSlwcrM6bQB0fMFXMrS4lIbEWlYcuSaovgNcIwfypSsCxMceV2c0C6AfC7ImXTIKspPOHGGCzcqcLy3wAAWU3DAG5X5DwG4HwAfgat4GSuBUUrqMnNXxWFdmmksDDL5kDRH3eEGAlLJEOWKRHMLUv0kyASVJWAuESFaLtAGw3QksCMQvpxf06RQhKBPR7XfT7UXzmtXMWvIWWoWFWqGqsESTyymnQVNWmuw0F2SKOGIlQEc0VDyE6qaKgpGvhUKHl1GFC2xE4kkKj3sAJj2qwwOtJye0BDmv4g4hXX2XXU3TWuHkZRXOVjzGpFm2JJeuo1hvF2HSHTmr23gjePTHSkuAT23VJFlhXLhQmE5yhqowjR+wjTozYNlDJpVRgpKCeGUAyDypZ31HEiFg3RcFmgesoxmqJs5pk3piVMqA4mUFGAVjtENACLtD1QM0uySCj3DP+AJvZoX1hGUm4DXDIieqVOIMoTzByJGEOxFhHz6E4lkEukKH73hXbz11mrSvUPj04njyvh9U-O1ocXgmejUxbnFl9pcMa1S1a3S3IoDt5j5kSFvmFkFoQjgp1RSBOh9VGA5zC3UHjvn0F2xyVJUGJD9gur6lJDWQbyriUVsHzgcnLv9xJPHRW1JrTp6E1uDuKEEMGnDvPgusGEMyZwOSmCmE7uz2x3JxoIRP7tRnkXsC1RwjuTgxOxWRvjDr6BeHnqW2JvkiVtXoQDKEGCeD4gdtYWUBV2mmsgpCxlYp51nyz31yYM0XJKWEZX5MmG02rlPMCLdXOD-NnrsRGWPsOvNsSutpht1l5OqQm0ENrVkBFpRXFjyQZzJAnnsBgYX3LRGIvtiAORsv4jlyztrVdrrS7MKSkKkLxtrDny7ucMSwhCa2TvawyyVNsgEw0uujCtT2RXEGLstHATsX9UmFi1gH5CoB-ovvOwtEnjTRXUuHPjZUYQNIljBNE0YNzyUKvz7u+uSkxiRssWRzHGAJ-wMKKPkBKN8K7NKBkKMeoKIyIGVr1UPXAV+TlnUFuk50GAQ1Hq4jxHTFZpSo6qfMdTA1djwZ0DuA3SVkCciqZWUT3xcFcbkPPtMdOKumJA3QZVGDnPLNuhQeMIhrMNSHSDkg83KuTtYbACifJJKMoQqWALtHJGEJRQP04lSD0AuEmFGCMqiKlQFViPiIcNTryYyNkHdo0sKRslyPgz3LGGzRKB6TiEqNcCAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'reasonsLoading',
        },
      ],
    },

    reasonsLoading: {
      invoke: {
        id: 'rootNew.reasonsLoading:invocation[0]',
        input: () => ({}),
        onDone: [
          {
            target: 'eventLoading',
            guard: {
              type: 'isEditBooking',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'sessionLoading',
            guard: {
              type: 'isSession',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'eventTemplateLoading',
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
        ],
        onError: {
          target: 'reasonsError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getReasons',
      },
    },

    reasonsError: {
      on: {
        retry: {
          target: 'reasonsLoading',
        },
      },
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'determineOwner',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if ((context.eventDetails as IEvent)?.id) {
            return {
              eventId: (context.eventDetails as IEvent)?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      on: {
        'language.update': {
          actions: [
            assign({
              language: ({event}) => event.language,
            }),
            sendTo(
              ({system}) => system.get('bookingMachine')!,
              ({event}) => ({
                type: 'language.update',
                language: event.language,
              }),
            ),
          ],
        },
      },
      invoke: {
        id: 'bookingFlow',
        input: ({context: {timezone, eventTemplate, language, session}}) => {
          if (timezone) {
            return {
              timezone,
              eventTemplate,
              language,
              session,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'requestMoreAvailabilitySuccess',
            guard: {
              type: 'isRequestMoreAvailabilitySuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellationAndNotSequential',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isRescheduleAndNotSequential',
          },
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
        {
          guard: {
            type: 'isSequentialEvent',
          },
          target: 'sequentialEventEditNotAllowed',
        },
      ],
      entry: assign({
        language: ({context}) => {
          const eventDetails = context.eventDetails as IEvent;
          const users =
            context.owner === 'HOST' || context.owner === 'INTERVIEWER'
              ? eventDetails?.interviewers
              : eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sequentialEventEditNotAllowed: {
      type: 'final',
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      always: [
        {
          target: 'sequentialBookingSuccess',
          guard: {
            type: 'isSequentialEvent',
          },
        },
        {
          target: 'generateAttendee',
        },
      ],
    },

    generateAttendee: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: (eventDetails as IEvent)?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },

    sequentialBookingSuccess: {
      type: 'final',
    },

    notifySuccess: {
      type: 'final',
    },

    requestMoreAvailabilitySuccess: {
      type: 'final',
    },

    cancellationFlow: {
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id) {
                    return {
                      eventId: eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHostOrInterviewer',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  const eventDetails = context.eventDetails as IEvent;
                  if (eventDetails?.id && context.aid) {
                    return {
                      eventId: eventDetails.id,
                      reason: event.reason || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId or aid not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          on: {
            'language.update': {
              actions: [
                assign({
                  language: ({event}) => event.language,
                }),
                sendTo(
                  ({system}) => system.get('bookingFlow'),
                  ({event}) => ({
                    type: 'language.update',
                    language: event.language,
                  }),
                ),
              ],
            },
          },
          invoke: {
            id: 'bookingFlow',
            input: ({context}) => {
              const {timezone, attendee, language} = context;
              if (timezone) {
                return {
                  timezone,
                  eventDetails: context.eventDetails as IEvent,
                  attendee,
                  language,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                  reason: ({event}) => event.output.rescheduleReason,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context}) => {
              const eventDetails = context.eventDetails as IEvent;
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {aid} = context;
          const eventDetails = context.eventDetails as IEvent;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          const eventDetails = context.eventDetails as IEvent;
          assertEvent(event, 'bookingCancelForm.submit');
          if (eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              reason: event.reason,
              viewerTimezone: eventDetails?.primary_guest_timezone || '',
              attendee: context.attendee || '',
              aid: eventDetails?.candidates?.[0]?.id || context.aid || '',
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
